// src/pages/OrdemProducao.js - Leituras de Ordem de Producao
//
// Cada linha da lista e uma LEITURA de codigo de barras de OP, nao uma ordem.
// A mesma OP costuma ser lida pela pesagem, pela encapsulacao e pelo peso
// medio, e toda linha carrega a quantidade TOTAL da ordem — por isso somar a
// coluna entre pessoas diferentes conta a mesma producao varias vezes.
//
// Clicando na linha, o painel expande e mostra a ordem de verdade, direto da
// FC18000 do Firebird: descricao, quantidade, volume, forma farmaceutica, tipo
// da ordem e os componentes. E o unico jeito de conferir se o numero da tela
// corresponde ao que a farmacia realmente produziu.
import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  Box, Card, CardContent, Typography, Grid, Button, TextField, Table,
  TableBody, TableCell, TableContainer, TableHead, TableRow, Paper,
  Chip, CircularProgress, Alert, IconButton, MenuItem, FormControl,
  InputLabel, Select, TablePagination, Tooltip, Collapse, Divider
} from '@mui/material';
import {
  Factory as FactoryIcon, Delete as DeleteIcon, Search as SearchIcon,
  Refresh as RefreshIcon, FilterList as FilterIcon, Clear as ClearIcon,
  KeyboardArrowDown as ExpandIcon, KeyboardArrowUp as CollapseIcon,
  ContentCopy as CopyIcon, Storage as StorageIcon, Warning as WarningIcon
} from '@mui/icons-material';
import { useAuth } from '../contexts/AuthContext';

const FILTROS_VAZIOS = {
  dataInicio: '', dataFim: '', setor: '', funcionario: '',
  nrord: '', tipoFormula: '', somenteDuplicadas: false, janelaDuplicada: 60
};

const OPCOES_VAZIAS = { tipos: [], setores: [], funcionarios: [] };

const formatarData = (valor) => {
  if (!valor) return null;
  const d = new Date(valor);
  return Number.isNaN(d.getTime()) ? null : d.toLocaleDateString('pt-BR');
};

const num = (v) => (v === null || v === undefined ? null : Number(v).toLocaleString('pt-BR'));

// Junta rotulo e nome so quando os dois existem. Sem isto, um TPFORMAFARMA
// nulo vindo do Firebird virava a string literal "null — null" na tela.
const par = (codigo, nome) => {
  if (codigo === null || codigo === undefined || codigo === '') return nome || null;
  return nome ? `${codigo} — ${nome}` : String(codigo);
};

// Fora do componente de proposito: definido dentro do render, cada setState
// criava um TIPO novo de componente e o React remontava a grade inteira.
const Campo = ({ rotulo, valor, destaque }) => (
  <Grid item xs={6} sm={4} md={3}>
    <Typography variant="caption" color="text.secondary" display="block">{rotulo}</Typography>
    <Typography variant="body2" fontWeight={destaque ? 'bold' : 'normal'}
      color={destaque ? 'primary.main' : 'text.primary'}>
      {valor === null || valor === undefined || valor === '' ? '-' : valor}
    </Typography>
  </Grid>
);

const OrdemProducao = () => {
  const { api, user } = useAuth();

  const [leituras, setLeituras] = useState([]);
  const [opcoes, setOpcoes] = useState(OPCOES_VAZIAS);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [aviso, setAviso] = useState('');
  const [success, setSuccess] = useState('');
  const [somaQuantidade, setSomaQuantidade] = useState(0);

  // Dois estados de proposito: `filtros` e o que esta nos campos e muda a cada
  // tecla; `filtrosAplicados` so muda quando o usuario manda aplicar. So o
  // segundo entra nas dependencias de `carregar`, entao digitar nao dispara
  // requisicao e o botao FILTRAR tem efeito de verdade.
  const [filtros, setFiltros] = useState(FILTROS_VAZIOS);
  const [filtrosAplicados, setFiltrosAplicados] = useState(FILTROS_VAZIOS);
  const [paginacao, setPaginacao] = useState({ pagina: 1, limite: 25, total: 0 });

  // Detalhe expandido: uma entrada por linha aberta, chaveada pelo id da leitura.
  const [expandida, setExpandida] = useState(null);
  const [detalhe, setDetalhe] = useState({});

  const timerFlash = useRef(null);
  useEffect(() => () => clearTimeout(timerFlash.current), []);

  const podeEditar = user?.nivel >= 3;

  const carregarOpcoes = useCallback(async () => {
    try {
      const r = await api.listarFiltrosOrdemProducao();
      const d = r?.dados || {};
      setOpcoes({
        tipos: d.tipos || [],
        setores: d.setores || [],
        funcionarios: d.funcionarios || []
      });
      setAviso('');
    } catch (err) {
      // Sem isso os tres selects ficam vazios e o usuario nao entende por que
      // nao consegue filtrar por funcionario, setor ou tipo.
      setAviso('Não foi possível carregar as opções dos filtros de funcionário, '
        + 'setor e tipo de fórmula. Os filtros de data e OP continuam funcionando.');
      setOpcoes(OPCOES_VAZIAS);
    }
  }, [api]);

  const carregar = useCallback(async () => {
    setLoading(true); setError('');
    try {
      const r = await api.listarOrdemProducao({
        ...filtrosAplicados,
        somenteDuplicadas: filtrosAplicados.somenteDuplicadas ? 'true' : '',
        pagina: paginacao.pagina,
        limite: paginacao.limite
      });
      if (!r.success) throw new Error(r.message);
      setLeituras(r.dados || []);
      setSomaQuantidade(r.soma_quantidade || 0);
      setPaginacao(p => (p.total === (r.paginacao?.total || 0)
        ? p
        : { ...p, total: r.paginacao?.total || 0 }));
    } catch (err) {
      setError(err.message);
      setLeituras([]);
    } finally {
      setLoading(false);
    }
  }, [api, filtrosAplicados, paginacao.pagina, paginacao.limite]);

  useEffect(() => { carregarOpcoes(); }, [carregarOpcoes]);
  // Ponto UNICO de fetch: qualquer mudanca de filtro aplicado ou de pagina
  // entra por aqui. Nao ha chamada direta a carregar() em lugar nenhum, o que
  // elimina a chance de duas requisicoes concorrentes.
  useEffect(() => { carregar(); }, [carregar]);

  const flash = (msg) => {
    setSuccess(msg);
    clearTimeout(timerFlash.current);
    timerFlash.current = setTimeout(() => setSuccess(''), 3000);
  };

  // Aplicar volta para a pagina 1 e troca os filtros de uma vez: um unico
  // setState composto evita dois renders e duas idas ao servidor.
  const aplicar = (novos) => {
    setFiltrosAplicados(novos);
    setPaginacao(p => (p.pagina === 1 ? p : { ...p, pagina: 1 }));
  };

  const aplicarFiltros = () => aplicar(filtros);

  const limparFiltros = () => {
    setFiltros(FILTROS_VAZIOS);
    aplicar(FILTROS_VAZIOS);
  };

  // Duplicatas e janela tem efeito imediato: sao ferramentas de inspecao, nao
  // faz sentido exigir um clique em FILTRAR depois.
  const alternarDuplicadas = () => {
    const novos = { ...filtros, somenteDuplicadas: !filtros.somenteDuplicadas };
    setFiltros(novos);
    aplicar(novos);
  };

  const aplicarJanela = () => {
    if (filtros.janelaDuplicada === filtrosAplicados.janelaDuplicada) return;
    aplicar({ ...filtrosAplicados, janelaDuplicada: filtros.janelaDuplicada });
  };

  // Busca o detalhe uma vez por linha e guarda: reabrir nao consulta o
  // Firebird de novo.
  const alternarExpansao = async (linha) => {
    if (expandida === linha.id) { setExpandida(null); return; }
    setExpandida(linha.id);
    if (detalhe[linha.id]?.ordem || detalhe[linha.id]?.leituras) return;

    setDetalhe(d => ({ ...d, [linha.id]: { loading: true } }));
    // allSettled e nao all: uma OP que nao existe no Firebird nao pode fazer
    // perder a lista de leituras, que vem do SQL Server e carregou bem.
    const [ordem, leiturasOrdem] = await Promise.allSettled([
      api.detalheOrdemFirebird(linha.CDFIL, linha.NRORD),
      api.leiturasDaOrdem(linha.CDFIL, linha.NRORD)
    ]);
    setDetalhe(d => ({
      ...d,
      [linha.id]: {
        loading: false,
        ordem: ordem.status === 'fulfilled' ? ordem.value?.dados : null,
        erro: ordem.status === 'rejected' ? ordem.reason?.message : null,
        leituras: leiturasOrdem.status === 'fulfilled' ? (leiturasOrdem.value?.dados || []) : []
      }
    }));
  };

  const excluir = async (linha) => {
    if (!window.confirm(
      `Excluir esta leitura?\n\n`
      + `OP ${linha.NRORD} — ${linha.funcionario_nome || linha.codigo_funcionario}\n`
      + `${linha.data_formatada} · ${linha.setor} · ${num(linha.quantidade)} ${linha.tipo_formula}\n\n`
      + `A ordem no Firebird não é tocada. Só o registro da leitura sai daqui, `
      + `e a exclusão fica gravada nos Logs.`)) return;
    try {
      const r = await api.excluirLeituraOrdem(linha.id);
      if (!r.success) throw new Error(r.message);

      // Solta o detalhe da linha que deixou de existir e recua a pagina se ela
      // era a ultima — senao o usuario fica olhando uma pagina vazia.
      if (expandida === linha.id) setExpandida(null);
      setDetalhe(d => { const c = { ...d }; delete c[linha.id]; return c; });

      const ultimaDaPagina = leituras.length === 1 && paginacao.pagina > 1;
      if (ultimaDaPagina) {
        setPaginacao(p => ({ ...p, pagina: p.pagina - 1 }));
      } else {
        carregar();
      }
      flash('Leitura removida');
    } catch (err) { setError(err.message); }
  };

  return (
    <Box sx={{ p: 3, backgroundColor: '#f8fafc', minHeight: '100vh' }}>
      <Paper elevation={0} sx={{
        p: 3, mb: 3, borderRadius: 2,
        background: 'linear-gradient(135deg, #0ea5e9 0%, #1e40af 100%)', color: 'white'
      }}>
        <Box display="flex" justifyContent="space-between" alignItems="center" flexWrap="wrap" gap={2}>
          <Box>
            <Typography variant="h4" fontWeight="bold" gutterBottom>
              <FactoryIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
              Ordem de Produção
            </Typography>
            <Typography variant="subtitle1" sx={{ opacity: 0.9 }}>
              Leituras de OP — clique na linha para ver a ordem no Firebird
            </Typography>
          </Box>
          <Box display="flex" gap={1} flexWrap="wrap">
            <Chip label={`${paginacao.total.toLocaleString('pt-BR')} leituras`}
              sx={{ backgroundColor: 'rgba(255,255,255,0.2)', color: 'white', fontWeight: 'bold' }} />
            <Tooltip title="Soma das quantidades das leituras filtradas. NÃO é produção: a mesma ordem é lida por vários setores e cada leitura carrega a quantidade cheia.">
              <Chip label={`${num(somaQuantidade)} somando as leituras`}
                sx={{ backgroundColor: 'rgba(255,255,255,0.2)', color: 'white', fontWeight: 'bold' }} />
            </Tooltip>
          </Box>
        </Box>
      </Paper>

      {error && <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError('')}>{error}</Alert>}
      {aviso && <Alert severity="warning" sx={{ mb: 3 }} onClose={() => setAviso('')}>{aviso}</Alert>}
      {success && <Alert severity="success" sx={{ mb: 3 }} onClose={() => setSuccess('')}>{success}</Alert>}

      <Alert severity="info" sx={{ mb: 3 }}>
        Cada linha é uma <strong>leitura</strong>, não uma ordem. A mesma OP é lida pela pesagem,
        pela encapsulação e pelo peso médio, e toda linha carrega a quantidade total da ordem —
        somar a coluna entre pessoas diferentes conta a mesma produção mais de uma vez.
      </Alert>

      {/* Filtros */}
      <Card elevation={0} sx={{ mb: 3, borderRadius: 2 }}>
        <CardContent>
          <Box display="flex" alignItems="center" mb={2}>
            <FilterIcon sx={{ mr: 1, color: '#64748b' }} />
            <Typography variant="h6" fontWeight="bold">Filtros</Typography>
          </Box>

          <Grid container spacing={2}>
            <Grid item xs={12} sm={6} md={2}>
              <TextField fullWidth size="small" type="date" label="Data Início"
                InputLabelProps={{ shrink: true }} value={filtros.dataInicio}
                onChange={(e) => setFiltros(f => ({ ...f, dataInicio: e.target.value }))} />
            </Grid>
            <Grid item xs={12} sm={6} md={2}>
              <TextField fullWidth size="small" type="date" label="Data Fim"
                InputLabelProps={{ shrink: true }} value={filtros.dataFim}
                onChange={(e) => setFiltros(f => ({ ...f, dataFim: e.target.value }))} />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <FormControl fullWidth size="small">
                <InputLabel>Funcionário</InputLabel>
                <Select value={filtros.funcionario} label="Funcionário"
                  onChange={(e) => setFiltros(f => ({ ...f, funcionario: e.target.value }))}>
                  <MenuItem value="">Todos</MenuItem>
                  {opcoes.funcionarios.map((f, i) => (
                    <MenuItem key={f.codigo_funcionario || `sem-codigo-${i}`}
                      value={f.codigo_funcionario || ''}>
                      {f.nome || f.codigo_funcionario || '(sem cadastro)'}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6} md={2}>
              <FormControl fullWidth size="small">
                <InputLabel>Setor</InputLabel>
                <Select value={filtros.setor} label="Setor"
                  onChange={(e) => setFiltros(f => ({ ...f, setor: e.target.value }))}>
                  <MenuItem value="">Todos</MenuItem>
                  {opcoes.setores.map(s => <MenuItem key={s} value={s}>{s}</MenuItem>)}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6} md={2}>
              <FormControl fullWidth size="small">
                <InputLabel>Tipo Fórmula</InputLabel>
                <Select value={filtros.tipoFormula} label="Tipo Fórmula"
                  onChange={(e) => setFiltros(f => ({ ...f, tipoFormula: e.target.value }))}>
                  <MenuItem value="">Todos</MenuItem>
                  {opcoes.tipos.map(t => <MenuItem key={t} value={t}>{t}</MenuItem>)}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6} md={1}>
              <TextField fullWidth size="small" label="OP" value={filtros.nrord}
                onChange={(e) => setFiltros(f => ({ ...f, nrord: e.target.value }))}
                onKeyDown={(e) => { if (e.key === 'Enter') aplicarFiltros(); }}
                InputProps={{ startAdornment: <SearchIcon sx={{ mr: 1, color: '#94a3b8' }} /> }} />
            </Grid>
          </Grid>

          <Box display="flex" alignItems="center" gap={2} mt={2} flexWrap="wrap">
            <Button variant="contained" startIcon={<FilterIcon />} onClick={aplicarFiltros}>
              Filtrar
            </Button>
            <Button variant="outlined" startIcon={<ClearIcon />} onClick={limparFiltros}>
              Limpar
            </Button>
            <Tooltip title="Recarregar com os filtros aplicados">
              <IconButton onClick={() => carregar()}><RefreshIcon /></IconButton>
            </Tooltip>

            <Divider orientation="vertical" flexItem sx={{ height: 40 }} />

            <Button
              variant={filtrosAplicados.somenteDuplicadas ? 'contained' : 'outlined'}
              color="warning" startIcon={<CopyIcon />} onClick={alternarDuplicadas}>
              Só registros repetidos
            </Button>
            <TextField
              size="small" type="number" label="Janela (s)" sx={{ width: 130 }}
              value={filtros.janelaDuplicada}
              onChange={(e) => setFiltros(f => ({ ...f, janelaDuplicada: e.target.value }))}
              onBlur={aplicarJanela}
              onKeyDown={(e) => { if (e.key === 'Enter') aplicarJanela(); }}
              helperText="mesma pessoa, mesma OP" />
          </Box>
        </CardContent>
      </Card>

      {/* Lista */}
      <Card elevation={0} sx={{ borderRadius: 2 }}>
        <CardContent>
          <Typography variant="h6" fontWeight="bold" gutterBottom>Leituras</Typography>

          {loading ? (
            <Box display="flex" justifyContent="center" py={5}><CircularProgress /></Box>
          ) : leituras.length === 0 ? (
            <Box textAlign="center" py={5}>
              <FactoryIcon sx={{ fontSize: 60, color: '#e2e8f0' }} />
              <Typography color="text.secondary">Nenhuma leitura encontrada</Typography>
            </Box>
          ) : (
            <TableContainer>
              <Table size="small">
                <TableHead sx={{ bgcolor: '#f8fafc' }}>
                  <TableRow>
                    <TableCell sx={{ width: 48 }} />
                    <TableCell sx={{ fontWeight: 'bold' }}>OP</TableCell>
                    <TableCell sx={{ fontWeight: 'bold' }}>Data/Hora</TableCell>
                    <TableCell sx={{ fontWeight: 'bold' }}>Funcionário</TableCell>
                    <TableCell sx={{ fontWeight: 'bold' }}>Setor</TableCell>
                    <TableCell sx={{ fontWeight: 'bold' }} align="right">Quantidade</TableCell>
                    <TableCell sx={{ fontWeight: 'bold' }}>Tipo</TableCell>
                    {podeEditar && <TableCell sx={{ fontWeight: 'bold' }} align="right">Ações</TableCell>}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {leituras.map(l => (
                    <React.Fragment key={l.id}>
                      <TableRow hover sx={{ cursor: 'pointer', '& > *': { borderBottom: 'unset' } }}
                        onClick={() => alternarExpansao(l)}>
                        <TableCell>
                          <IconButton size="small">
                            {expandida === l.id ? <CollapseIcon /> : <ExpandIcon />}
                          </IconButton>
                        </TableCell>
                        <TableCell>
                          <Chip size="small" label={l.NRORD} color="primary" variant="outlined" />
                          <Typography variant="caption" color="text.secondary" display="block">
                            {l.codigo_lido}
                          </Typography>
                        </TableCell>
                        <TableCell>{l.data_formatada}</TableCell>
                        <TableCell>
                          {l.funcionario_nome || l.codigo_funcionario}
                          <Typography variant="caption" color="text.secondary" display="block">
                            {l.codigo_funcionario}
                          </Typography>
                        </TableCell>
                        <TableCell>{l.setor}</TableCell>
                        <TableCell align="right">
                          <Typography fontWeight="bold">{num(l.quantidade)}</Typography>
                        </TableCell>
                        <TableCell>
                          <Chip size="small" label={l.tipo_formula || '-'} />
                          {l.duplicada && (
                            <Tooltip title={`Esta mesma pessoa registrou a mesma OP ${l.leituras_iguais} vezes no mesmo setor, dentro da janela configurada. Provável clique repetido — cada linha soma de novo no relatório.`}>
                              <Chip size="small" color="warning" icon={<WarningIcon />}
                                label={`${l.leituras_iguais}x`} sx={{ ml: 0.5 }} />
                            </Tooltip>
                          )}
                        </TableCell>
                        {podeEditar && (
                          <TableCell align="right">
                            <Tooltip title="Excluir esta leitura">
                              <IconButton size="small" color="error"
                                onClick={(e) => { e.stopPropagation(); excluir(l); }}>
                                <DeleteIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          </TableCell>
                        )}
                      </TableRow>

                      <TableRow>
                        <TableCell colSpan={podeEditar ? 8 : 7} sx={{ py: 0, border: 0 }}>
                          <Collapse in={expandida === l.id} timeout="auto" unmountOnExit>
                            <Box sx={{ my: 2, p: 2, bgcolor: '#f8fafc', borderRadius: 2 }}>
                              {detalhe[l.id]?.loading && (
                                <Box display="flex" alignItems="center" gap={2} py={2}>
                                  <CircularProgress size={20} />
                                  <Typography variant="body2">Consultando o Firebird...</Typography>
                                </Box>
                              )}

                              {detalhe[l.id]?.erro && (
                                <Alert severity="warning" sx={{ mb: 2 }}>{detalhe[l.id].erro}</Alert>
                              )}

                              {detalhe[l.id]?.ordem && (
                                <>
                                  <Box display="flex" alignItems="center" gap={1} mb={1}>
                                    <StorageIcon fontSize="small" color="action" />
                                    <Typography variant="subtitle2" fontWeight="bold">
                                      Ordem no Firebird (FC18000)
                                    </Typography>
                                  </Box>
                                  <Typography variant="h6" gutterBottom>
                                    {detalhe[l.id].ordem.descricao || '(sem descrição)'}
                                  </Typography>

                                  <Grid container spacing={2} sx={{ mb: 2 }}>
                                    <Campo rotulo="QUANT" valor={num(detalhe[l.id].ordem.quantidade)} destaque />
                                    <Campo rotulo="VOLUME"
                                      valor={num(detalhe[l.id].ordem.volume) === null
                                        ? null
                                        : `${num(detalhe[l.id].ordem.volume)} ${detalhe[l.id].ordem.unidade_volume || ''}`.trim()} />
                                    <Campo rotulo="TPFORMAFARMA"
                                      valor={par(detalhe[l.id].ordem.tpformafarma, detalhe[l.id].ordem.forma_nome)} />
                                    <Campo rotulo="TPORD"
                                      valor={par(detalhe[l.id].ordem.tpord, detalhe[l.id].ordem.tpord_nome)} />
                                    <Campo rotulo="DTORD" valor={formatarData(detalhe[l.id].ordem.data_ordem)} />
                                    <Campo rotulo="Validade" valor={formatarData(detalhe[l.id].ordem.data_validade)} />
                                    <Campo rotulo="CDFRM" valor={detalhe[l.id].ordem.cdfrm} />
                                    <Campo rotulo="Lote" valor={detalhe[l.id].ordem.lote} />
                                    <Campo rotulo="Filial" valor={detalhe[l.id].ordem.cdfil} />
                                    <Campo rotulo="Filial destino" valor={detalhe[l.id].ordem.cdfild} />
                                    <Campo rotulo="Ficha impressa" valor={detalhe[l.id].ordem.flag_ficha} />
                                    <Campo rotulo="Manipulador (CDFUNMA)" valor={detalhe[l.id].ordem.cdfunma} />
                                  </Grid>

                                  {String(detalhe[l.id].ordem.tpord || '').toUpperCase() === 'S' && (
                                    <Alert severity="warning" sx={{ mb: 2 }}>
                                      Ordem do tipo <strong>S (solução / insumo)</strong> — não é produto
                                      acabado. Álcool, bases e soluções entram aqui e, somados junto com
                                      cápsulas na mesma coluna, distorcem o ranking de produção.
                                    </Alert>
                                  )}

                                  {detalhe[l.id].ordem.itens?.length > 0 && (
                                    <>
                                      <Typography variant="subtitle2" fontWeight="bold" gutterBottom>
                                        Componentes ({detalhe[l.id].ordem.itens.length})
                                      </Typography>
                                      <TableContainer sx={{ mb: 2, bgcolor: 'white', borderRadius: 1 }}>
                                        <Table size="small">
                                          <TableHead>
                                            <TableRow>
                                              <TableCell>Item</TableCell>
                                              <TableCell>Descrição</TableCell>
                                              <TableCell align="right">Qtd</TableCell>
                                              <TableCell align="right">Pesado</TableCell>
                                              <TableCell>Un</TableCell>
                                              <TableCell>Lote</TableCell>
                                            </TableRow>
                                          </TableHead>
                                          <TableBody>
                                            {detalhe[l.id].ordem.itens.map((i, idx) => (
                                              <TableRow key={`${i.itemid}-${i.cdpro}-${idx}`}>
                                                <TableCell>{i.itemid}</TableCell>
                                                <TableCell>{i.descricao}</TableCell>
                                                <TableCell align="right">{num(i.quantidade) ?? '-'}</TableCell>
                                                <TableCell align="right">{num(i.qt_pesada) ?? '-'}</TableCell>
                                                <TableCell>{i.unidade}</TableCell>
                                                <TableCell>{i.lote}</TableCell>
                                              </TableRow>
                                            ))}
                                          </TableBody>
                                        </Table>
                                      </TableContainer>
                                    </>
                                  )}
                                </>
                              )}

                              {detalhe[l.id]?.leituras?.length > 0 && (
                                <>
                                  <Typography variant="subtitle2" fontWeight="bold" gutterBottom>
                                    Quem leu esta ordem ({detalhe[l.id].leituras.length})
                                  </Typography>
                                  <TableContainer sx={{ bgcolor: 'white', borderRadius: 1 }}>
                                    <Table size="small">
                                      <TableHead>
                                        <TableRow>
                                          <TableCell>Funcionário</TableCell>
                                          <TableCell>Setor</TableCell>
                                          <TableCell>Data/Hora</TableCell>
                                          <TableCell align="right">Quantidade creditada</TableCell>
                                        </TableRow>
                                      </TableHead>
                                      <TableBody>
                                        {detalhe[l.id].leituras.map(x => (
                                          <TableRow key={x.id}
                                            sx={{ bgcolor: x.id === l.id ? '#e0f2fe' : 'inherit' }}>
                                            <TableCell>{x.funcionario_nome || x.codigo_funcionario}</TableCell>
                                            <TableCell>{x.setor}</TableCell>
                                            <TableCell>{x.data_formatada}</TableCell>
                                            <TableCell align="right">{num(x.quantidade)}</TableCell>
                                          </TableRow>
                                        ))}
                                      </TableBody>
                                    </Table>
                                  </TableContainer>
                                  <Typography variant="caption" color="text.secondary">
                                    Todas recebem a quantidade cheia da ordem. É o comportamento do
                                    sistema, não um erro de digitação.
                                  </Typography>
                                </>
                              )}
                            </Box>
                          </Collapse>
                        </TableCell>
                      </TableRow>
                    </React.Fragment>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}

          <TablePagination
            component="div"
            count={paginacao.total}
            page={Math.max(paginacao.pagina - 1, 0)}
            onPageChange={(e, p) => setPaginacao(x => ({ ...x, pagina: p + 1 }))}
            rowsPerPage={paginacao.limite}
            onRowsPerPageChange={(e) => setPaginacao(x => ({
              ...x, limite: parseInt(e.target.value, 10), pagina: 1
            }))}
            rowsPerPageOptions={[10, 25, 50, 100]}
            labelRowsPerPage="Linhas por página:"
            labelDisplayedRows={({ from, to, count }) => `${from}-${to} de ${count}`}
          />
        </CardContent>
      </Card>
    </Box>
  );
};

export default OrdemProducao;
